import { Popper } from "@mui/material";
import {
  FulfillmentOrderLineItem,
  groupFulfillmentOrderLineItems,
} from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import OverflowIcon from "@redotech/redo-icons/layers-two-01.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { ButtonSize, IconButton } from "@redotech/redo-web/button";
import CircleSpinner from "@redotech/redo-web/circle-spinner.svg";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useRef, useState } from "react";
import { ItemsTable } from "../../common/items-table";
import * as itemNameCellCss from "./item-name-cell.module.css";

export const ItemNameCell = memo(function ItemNameCell({
  lineItems,
  isWaitingForSplit = false,
  showBin = false,
}: {
  lineItems: FulfillmentOrderLineItem[];
  isWaitingForSplit?: boolean;
  showBin?: boolean;
}) {
  const team = useContext(TeamContext);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );
  const timeoutRef = useRef<NodeJS.Timeout>();

  if (lineItems.length === 0) {
    return null;
  }

  return (
    <>
      <Flex
        align="center"
        dir="row"
        justify="space-between"
        onMouseEnter={() => {
          timeoutRef.current = setTimeout(() => {
            setIsDropdownOpen(true);
          }, 700);
        }}
        onMouseLeave={() => {
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          setIsDropdownOpen(false);
        }}
        p="lg"
        ref={setDropdownAnchor}
      >
        {lineItems.length > 1 ? (
          <Flex flexDirection="row" gap="md">
            <Flex flexDirection="column" justify="center">
              <Text fontSize="xs" textColor="secondary">
                {lineItems.length}
              </Text>
            </Flex>
            {isWaitingForSplit && (
              <Flex
                className={itemNameCellCss.spinnerWrapper}
                flexDirection="column"
                justify="center"
              >
                <CircleSpinner />
              </Flex>
            )}
          </Flex>
        ) : (
          <ItemInfo
            isWaitingForSplit={isWaitingForSplit}
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            lineItem={lineItems[0]}
            showSpinner
          />
        )}
        {lineItems.length > 1 && (
          <IconButton disabled size={ButtonSize.SMALL}>
            <OverflowIcon />
          </IconButton>
        )}
      </Flex>
      <Popper anchorEl={dropdownAnchor} open={isDropdownOpen}>
        <div className={itemNameCellCss.dropdown}>
          <ItemsTable
            disableTooltip
            items={groupFulfillmentOrderLineItems(lineItems, {
              degroupByCustomAttributes:
                team?.settings.outboundLabels?.degroupItemsByCustomAttributes,
            })}
            showBin={showBin}
            team={team}
          />
        </div>
      </Popper>
    </>
  );
});

export const ItemInfo = memo(function ItemInfo({
  lineItem,
  isWaitingForSplit,
  showSpinner,
}: {
  lineItem: FulfillmentOrderLineItem;
  isWaitingForSplit?: boolean;
  showSpinner?: boolean;
}) {
  const title = lineItem.productTitle || lineItem.title;
  const variantTitle = lineItem.variantTitle;
  const imageUrl = lineItem.imageUrl;

  return (
    <Flex gap="md">
      {imageUrl && (
        <Flex
          className={itemNameCellCss.imageWrapper}
          flexDirection="column"
          justify="center"
        >
          <img className={itemNameCellCss.productImage} src={imageUrl ?? ""} />
        </Flex>
      )}
      <Flex justify="space-between" w="full">
        <Flex flexDirection="column" gap="xs" justify="center">
          <Text fontSize="xs" fontWeight="medium">
            {title}
          </Text>
          {variantTitle &&
            variantTitle !== "Default Title" &&
            lineItem.productTitle && (
              <Text fontSize="xxs" textColor="tertiary">
                {variantTitle.replace(" / ", ", ")}
              </Text>
            )}
        </Flex>
      </Flex>
      {isWaitingForSplit && showSpinner && (
        <Flex className={itemNameCellCss.spinnerWrapper} justify="center">
          <CircleSpinner />
        </Flex>
      )}
    </Flex>
  );
});
